import React, { useCallback } from "react";

import { library } from "@fortawesome/fontawesome-svg-core";
import { fas } from "@fortawesome/free-solid-svg-icons";
import { fab } from "@fortawesome/free-brands-svg-icons";
import { useTranslation } from "react-i18next";
import { getDasUrl } from "../../actions/config";
import UseWidgetVideo from "./Widgets/UseWidgetVideo";
import UseWidgetWeather from "./Widgets/UseWidgetWeather";
import UseWidgetRooms from "./Widgets/UseWidgetRooms";
import UseWidgetWifiLogin from "./Widgets/UseWidgetWifiLogin";
import UseWidgetPortal from "./Widgets/UseWidgetPortal";
import useLandingSession from "../../hooks/useLandingSession";
import WidgetHandler from "./WidgetHandler";
import useWidget from "../../hooks/useWidget";
import { arrangeThemeStyles, configToCSS, processActions } from "./DesignUtils";
import { useDispatch } from "react-redux";
import { setSelectedLanding } from "actions/hotel";
// import { useSelector } from "react-redux";
// import { getHotel } from "../../actions/hotel";
import { useStats } from "contexts/stats";
import { STATS_EVENT_TYPES } from "zstats";
import { WidgetType } from "zrender";
// import GridOverlaySVG, { inferContainerGrid } from "views/GridOverlaySVG";

const UseWidgetContainer = ({
    id,
    type,
    currentPosition,
    data,
    nesteds,
    goToLanding,
    goToRoomShop,
    goToMessages,
    goToCuiner,
    promos,
    index = null,
    parentElement,
    size = { W: 0, H: 0 },
}) => {
    const { i18n } = useTranslation();
    const { landing } = useLandingSession();
    const stats = useStats();
    const dispatch = useDispatch();
    const lang = (i18n.language || "").match(/^[^-]+/)[0];
    const dasURL = getDasUrl();
    const statsTag = data?.statsTag || null;

    const { cfg } = useWidget({
        data: data && data.config && data.config.container ? data.config.container : null,
        styles: data && data.config && data.config.styles ? data.config.styles : null,
        parseConfig,
    });

    const containerImage = () => {
        const dasUrl = getDasUrl();

        const container = data?.config?.container;
        const libraryRef = container?.resource?.libraryRef;
        const externalUrl = container?.resource?.externalUrl;

        const url = externalUrl ?? (libraryRef ? `${dasUrl}/${libraryRef}` : null);

        return {
            backgroundImage: url ? `url(${url})` : "none",
            backgroundSize: "cover",
            backgroundPosition: "50% 50%",
            backgroundRepeat: "no-repeat",
        };
    };

    let response = null;
    let link = { target: null, id: null, href: null, onClick: null };
    let dynamicObject = [];

    library.add(fas);
    library.add(fab);

    link = processActions({
        data,
        id,
        landing,
        lang,
        goToLanding,
        goToCuiner,
        goToRoomShop,
        dasURL,
        link,
        setSelectedLanding: (l) => {
            dispatch(setSelectedLanding(l));
        },
    });

    const getLink = useCallback(
        (childData) =>
            processActions({
                data: childData,
                id,
                landing,
                lang,
                goToLanding,
                goToCuiner,
                goToRoomShop,
                dasURL,
                setSelectedLanding: (l) => {
                    dispatch(setSelectedLanding(l));
                },
                link: { target: null, id: null, href: null, onClick: null },
            }),
        [id, landing, lang, goToLanding, goToCuiner, goToRoomShop, dasURL],
    );

    switch (type) {
        case "weather":
            response = (
                <UseWidgetWeather
                    key={"key-widget-" + data.id}
                    config={data.config}
                    id={data.id}
                    deviceType={data.deviceType}
                />
            );
            break;
        case "rooms":
            response = (
                <UseWidgetRooms
                    key={"key-widget-" + data.id}
                    config={data.config}
                    id={data.id}
                    deviceType={data.deviceType}
                />
            );
            link.href = "";
            break;

        case "portal":
            response = (
                <UseWidgetPortal key={"key-widget-" + data.id} config={data.config} deviceType={data.deviceType} />
            );
            break;
        case "wifilogin":
            response = (
                <UseWidgetWifiLogin
                    key={"key-widget-" + data.id}
                    config={data.config}
                    id={data.id}
                    deviceType={data.deviceType}
                />
            );
            break;
        case "image":
        case "icon":
        case "iframe":
        case "html":
        case "chatbutton":
        case "slider":
        case "messages":
        case "button":
        case "promo":
        case "video":
        case "logo":
        case "text":
        case "carousel":
            response = (
                <WidgetHandler
                    type={type.toUpperCase()}
                    lang={lang}
                    data={data}
                    size={size}
                    promos={promos}
                    getLink={getLink}
                    parentElement={parentElement}
                ></WidgetHandler>
            );
            break;

        case "container": {
            const calculateSize = (size, parts, gap) => {
                const gapSize = (gap || 0) * Math.max(0, parts - 1);
                const fullSize = size - gapSize;
                const exact = fullSize / Math.max(1, parts);
                if ((fullSize < 0 || exact < 1) && gap > 0) {
                    return calculateSize(size, parts, gap - 1);
                }
                return { size: Math.max(1, exact), gap };
            };

            let itemsMargin = {};
            const currentPositionContainer = { ...currentPosition, left: 0, top: 0 };

            const c = data?.config?.container;

            if (c?.margin) {
                const mW = Number(c.margin.W) || 0;
                const mH = Number(c.margin.H) || 0;
                if (mW > 0) {
                    currentPositionContainer.left = mW;
                    currentPositionContainer.width -= mW * 2;
                }
                if (mH > 0) {
                    currentPositionContainer.top = mH;
                    currentPositionContainer.height -= mH * 2;
                }
            }
            if (c?.itemsMargin) {
                const imW = Number(c.itemsMargin.W) || 0;
                const imH = Number(c.itemsMargin.H) || 0;
                if (imW > 0) {
                    itemsMargin.paddingLeft = imW;
                    itemsMargin.paddingRight = imW;
                }
                if (imH > 0) {
                    itemsMargin.paddingTop = imH;
                    itemsMargin.paddingBottom = imH;
                }
            }

            const gapX = Number(c?.gap?.x ?? c?.gapX ?? 0) || 0;
            const gapY = Number(c?.gap?.y ?? c?.gapY ?? 0) || 0;

            const g = c?.grid || {};
            const cfgCols = size.W || 0;
            const cfgRows = size.H || 0;

            const reqCols = Math.max(
                1,
                ...(nesteds ?? []).map((n) => (Number(n?.position?.X ?? 0) || 0) + (Number(n?.size?.W ?? 1) || 1)),
            );
            const reqRows = Math.max(
                1,
                ...(nesteds ?? []).map((n) => (Number(n?.position?.Y ?? 0) || 0) + (Number(n?.size?.H ?? 1) || 1)),
            );

            const hasDeclared = cfgCols > 0 && cfgRows > 0;

            const finalCols = hasDeclared ? cfgCols : reqCols;
            const finalRows = hasDeclared ? cfgRows : reqRows;

            if (hasDeclared && (reqCols > cfgCols || reqRows > cfgRows)) {
                console.warn(`Container ${c?.id} grid EXCEEDED by children`, {
                    cfgCols,
                    cfgRows,
                    reqCols,
                    reqRows,
                });
            }

            const innerW = Math.max(0, Number(currentPositionContainer.width) || 0);
            const innerH = Math.max(0, Number(currentPositionContainer.height) || 0);

            const { size: colSize, gap: finalGapX } = calculateSize(innerW, finalCols, gapX);
            const { size: rowSize, gap: finalGapY } = calculateSize(innerH, finalRows, gapY);

            const children = (nesteds ?? []).map((nested, index) => {
                if (!nested) return null;

                let X = Number(nested.position?.X ?? 0) || 0;
                let Y = Number(nested.position?.Y ?? 0) || 0;
                let W = Number(nested.size?.W ?? 1) || 1;
                let H = Number(nested.size?.H ?? 1) || 1;

                const left = currentPositionContainer.left + X * (colSize + finalGapX);
                const top = currentPositionContainer.top + Y * (rowSize + finalGapY);
                const width = W * colSize + Math.max(0, W - 1) * finalGapX;
                const height = H * rowSize + Math.max(0, H - 1) * finalGapY;

                const wrapperStyle = {
                    position: "absolute",
                    left,
                    top,
                    width,
                    height,
                    backgroundColor: "transparent",
                    overflow: nested.type === "text" || nested.type === "messages" ? "visible" : "hidden",
                    fontFamily: "",
                    boxSizing: "border-box",
                };

                const innerStyle = {
                    width: "100%",
                    height: "100%",
                    ...itemsMargin,
                    boxSizing: "border-box",
                };

                if (nested.type === "text") {
                    const heightScreen = typeof window !== "undefined" ? window.innerHeight : 900;
                    const extra =
                        heightScreen <= 700
                            ? heightScreen / 100
                            : heightScreen <= 750
                              ? heightScreen / 130
                              : heightScreen <= 800
                                ? heightScreen / 160
                                : heightScreen <= 850
                                  ? heightScreen / 190
                                  : heightScreen <= 900
                                    ? heightScreen / 220
                                    : 0;

                    const containerMarginY = Number(c?.margin?.H) ? Number(c.margin.H) / 10 : 0;
                    wrapperStyle.height = height + extra + containerMarginY;
                }

                const widgetDataStyle = nested?.style
                    ? arrangeThemeStyles({
                          elementStyles: JSON.parse(nested?.style),
                          theme: landing ? landing.theme : null,
                          widgetStyles: false,
                      })
                    : null;

                const widgetData = {
                    type: nested.type,
                    data: {
                        style: widgetDataStyle, //nested.style,
                        config: { ...nested.config, styles: nested.style, parentCfg: c },
                        id: `container-${id}-${nested.type}-${index}`,
                        data: nested.config[nested.type],
                        actions: nested.actions.filter((action) => action.type && action.value),
                        fonts: nested.fonts,
                        deviceType: data.deviceType,
                    },
                    nesteds: nested.widgets,
                };

                return (
                    <div key={widgetData.data.id} style={wrapperStyle}>
                        <div style={innerStyle}>
                            <UseWidgetContainer
                                id={`${id}-child-${index}`}
                                type={widgetData.type}
                                parentElement={WidgetType.Container}
                                currentPosition={currentPositionContainer}
                                data={widgetData.data}
                                nesteds={widgetData.nesteds}
                                goToLanding={goToLanding}
                                goToRoomShop={goToRoomShop}
                                goToMessages={goToMessages}
                                goToCuiner={goToCuiner}
                                promos={promos}
                            />
                        </div>
                    </div>
                );
            });

            response = (
                <div
                    key={`key-widget-${data.id}`}
                    id={`${data.id}`}
                    className="flex w-full h-full"
                    style={{ position: "relative", ...configToCSS(cfg, data.deviceType), ...containerImage() }}
                >
                    {children}
                </div>
            );
            break;
        }

        default:
    }

    const excludedLinkTypes = [
        WidgetType.Image,
        WidgetType.iFrame,
        WidgetType.HTML,
        WidgetType.ChatButton,
        WidgetType.Slider,
        WidgetType.Messages,
        WidgetType.Button,
        WidgetType.Promo,
    ];
    const hasLink = link && (link.href || typeof link.onClick === "function");

    if (hasLink && !excludedLinkTypes.includes(type.toUpperCase())) {
        const href = link.href || "#";
        response = (
            <a
                key="link"
                id={link.id}
                href={href}
                target={link.target}
                onClick={(e) => {
                    if (link?.type === "library") {
                        stats.addEvent({
                            type: STATS_EVENT_TYPES.SECTION,
                            eventName: "library",
                        });
                    }

                    if (statsTag) {
                        stats.addEvent({ eventName: statsTag, type: STATS_EVENT_TYPES.CLICK });
                    }
                    if (!link.href || link.href === "#") e.preventDefault();
                    if (typeof link.onClick === "function") link.onClick();
                }}
            >
                {response}
            </a>
        );
    }
    return response;
};

const parseConfig = ({ styles, theme }) => {
    const dasURL = getDasUrl();
    const fallbackBorderColor = theme?.colors?.defaultForeground;
    return {
        bgColor: styles.bgColor || theme.colors.defaultBackground,
        bgImage: {
            src:
                styles?.images?.length > 0 && styles.images[0].libraryRef
                    ? `${dasURL}/${styles.images[0].libraryRef}`
                    : styles?.images?.length > 0 && styles.images[0].externalUrl,
            adjust: "cover",
            position: "50",
        },
        border: {
            color: styles.borderColor || fallbackBorderColor,
            size: styles.borderWidth,
            radius: styles.radius,
        },
    };
};

export default UseWidgetContainer;
